import { MARKETS } from './markets.ts';

export const HEADER_NAMES = Object.freeze({
  CASUMO_BRAND_ID: 'X-Brand',
  USER_AGENT:      'X-FE-User-Agent',
});

export const API_ERRORS = Object.freeze({
  EMAIL_TAKEN:                    'EMAIL_TAKEN',
  PHONE_NUMBER_ALREADY_EXISTS:    'PHONE_NUMBER_ALREADY_EXISTS',
  VALID:                          'VALID',
  INVALID_JURISDICTION_FOR_BRAND: 'INVALID_JURISDICTION_FOR_BRAND',
  COUNTRY_RESTRICTED:             'COUNTRY_RESTRICTED',
  PROVINCE_IN_ADDRESS_REQUIRED:   'PROVINCE_IN_ADDRESS_REQUIRED',
  INVALID_OCR_DATA:               'INVALID_OCR_DATA',
  CREDENTIALS_TAKEN:              'CREDENTIALS_TAKEN',
  UNIQUE_PLAYER:                  'UNIQUE_PLAYER',
  SOCIAL_SECURITY_NUMBER_ALREADY_EXISTS:
    'SOCIAL_SECURITY_NUMBER_ALREADY_EXISTS',
  TOO_YOUNG:                            'TOO_YOUNG',
  SELF_EXCLUDED_WITH_NATIONAL_REGISTRY: 'SELF_EXCLUDED_WITH_NATIONAL_REGISTRY',
  REGISTRATION_METHOD_COUNTRY_MISMATCH: 'REGISTRATION_METHOD_COUNTRY_MISMATCH',
  CAPTCHA_INVALID_RESPONSE:             'CAPTCHA_INVALID_RESPONSE',
  POSTAL_CODE_FORBIDDEN:                'POSTAL_CODE_FORBIDDEN',
  JURISDICTION_NOT_FOUND:               'JURISDICTION_NOT_FOUND',
  LIMIT_TOO_LOW:                        'LIMIT_TOO_LOW',
  LIMIT_TOO_HIGH:                       'LIMIT_TOO_HIGH',
  LIMIT_REQUIRED:                       'LIMIT_REQUIRED',
  INVALID_IDENTITY:                     'invalid_identity',
  GENERIC_ERROR:                        'GENERIC_ERROR',
});

export const OPTED_OUT_WELCOME_OFFER_ID = 'opted-out-wo-registration';

export const MARKETING_CONSENT_STATUS = Object.freeze({
  GRANTED: 'granted',
  PARTIAL: 'partial',
  DENIED:  'denied',
} as const);

// Tooltip shown when a user interacts with a disabled (regulatory) province.
export const DISABLED_PROVINCE_TOOLTIP = 'Registration unavailable in this province';

export const COOKIE_NAMES = Object.freeze({
  TIME_SPENT:           'time_spent',
  FIRST_LOGIN:          'first_login',
  IS_NATIVE_COOKIE:     'is_native_cookie',
  KAZUMO:               'kazumo',
  KAZUMO_TRACKING_ID:   'kazumoTrackingId',
  KAZUMO_MEDIUM:        'kazumoMedium',
  NATIVE_PAY_AND_PLAY:  'native_pay_and_play',
  KAZUMOKEYCLOAK:       'kazumoKeycloak',
  REGISTRATIONFORM:     'registrationform',
  NATIVE_EVENTS_COOKIE: 'native_events_cookie',
  CASUMO_COMMON_COOKIE: 'casumo_common_cookie',
  CMAPI_COOKIE_PRIVACY: 'cmapi_cookie_privacy',
  WELCOME_LOGIN:        'welcome_login',
});

export const DATE_FORMATS = Object.freeze({
  DD_MM_YYYY: 'dd/MM/yyyy',
  MM_DD_YYYY: 'MM/dd/yyyy',
  YYYY_MM_DD: 'yyyy-MM-dd',
});

export const REGISTRATION_FLOW_ENDPOINTS = Object.freeze({
  [MARKETS.SV]:   'sga/register/se',
  [MARKETS.ENGB]: 'ukgc/register/gb',
  [MARKETS.ES]:   'dgoj/register',
  [MARKETS.GI]:   'gra/register',
  [MARKETS.FI]:   'gra/register',
  [MARKETS.ENNZ]: 'gra/register',
  [MARKETS.ENCA]: 'gra/register/ca',
  default:        'mga/register',
});

export const ENVIRONMENTS = Object.freeze({
  PROD: 'production',
  TEST: 'test',
  DEV:  'dev',
});

export const NAME_FIELD_MAX_LENGTH = 65;

export const NAME_FIELDS = Object.freeze([
  'firstName',
  'lastName',
  'secondLastName',
]);

export const DEPOSIT_LIMIT_FIELD_MAX_LENGTH = 30;

const DATA_TEST_ID_PREFIX = 'registration-';

export const TEST_ID = Object.freeze({
  PREFIX:                  DATA_TEST_ID_PREFIX,
  SELECT:                  `${DATA_TEST_ID_PREFIX}select-`,
  TITLE:                   `${DATA_TEST_ID_PREFIX}title-`,
  DESCRIPTION:             `${DATA_TEST_ID_PREFIX}description-`,
  SEARCH:                  `${DATA_TEST_ID_PREFIX}search-`,
  INPUT:                   `${DATA_TEST_ID_PREFIX}input-`,
  BUTTON:                  `${DATA_TEST_ID_PREFIX}button-`,
  TOGGLE:                  `${DATA_TEST_ID_PREFIX}toggle-`,
  COMBOBOX_TRIGGER:        `${DATA_TEST_ID_PREFIX}combobox-trigger-`,
  COUNTRY:                 `${DATA_TEST_ID_PREFIX}country-`,
  PROVINCE:                `${DATA_TEST_ID_PREFIX}province-`,
  BUTTON_PRIMARY:          `${DATA_TEST_ID_PREFIX}button-primary`,
  BUTTON_SECONDARY:        `${DATA_TEST_ID_PREFIX}button-secondary`,
  BUTTON_COUNTRY_SELECTOR: `${DATA_TEST_ID_PREFIX}country-selector`,
  BUTTON_NO_BONUS:         `${DATA_TEST_ID_PREFIX}button-no-bonus`,
  BUTTON_CLOSE:            `${DATA_TEST_ID_PREFIX}close-button`,
  ERROR:                   `${DATA_TEST_ID_PREFIX}error-`,
  PNP:                     `${DATA_TEST_ID_PREFIX}pnp-`,
  PHONE_PREFIX:            `${DATA_TEST_ID_PREFIX}phone-prefix-`,
  SEARCH_RESULT:           `${DATA_TEST_ID_PREFIX}search-result-`,
  TEXT:                    `${DATA_TEST_ID_PREFIX}text`,
  IMAGE:                   `${DATA_TEST_ID_PREFIX}image-`,
})

// These errors contain the proper fieldName from the CMS errors object
export const REGISTRATION_ERRORS = Object.freeze({
  FIELD_IS_REQUIRED:                  'field_is_required',
  ADDRESS_NOT_FOUND:                  'address_not_found',
  ADDRESS_NOT_MATCHED:                'address_did_not_match_the_records',
  UNKNOWN:                            'unknown_error',
  EMAIL_IS_REQUIRED:                  'email_required',
  PASSWORD_IS_REQUIRED:               'password_required',
  PHONE_IS_REQUIRED:                  'phone_required',
  BIRTH_DATE_IS_REQUIRED:             'birth_date_required',
  BIRTH_PLACE_IS_REQUIRED:            'birth_place_required',
  GENDER_IS_REQUIRED:                 'gender_required',
  DUPLICATE_EMAIL:                    'duplicate_email',
  DUPLICATE_PHONE:                    'duplicate_phone',
  INCORRECT_EMAIL:                    'incorrect_email',
  EMAIL_FORBIDDEN:                    'email_forbidden',
  INCORRECT_PASSWORD:                 'incorrect_password',
  INCORRECT_PHONE_NUMBER:             'incorrect_phone_number',
  INCORRECT_BIRTH_DAY:                'incorrect_birth_day',
  UNDERAGE:                           'user_under_age',
  AGE_ERROR:                          'user_age_error',
  DEPOSIT_REQUIRED:                   'deposit_required',
  DEPOSIT_TOO_LOW:                    'deposit_too_low',
  DEPOSIT_TOO_HIGH:                   'deposit_too_high',
  LIMIT_TOO_LOW:                      'limit_too_low',
  LIMIT_TOO_HIGH:                     'limit_too_high',
  LIMIT_IS_REQUIRED:                  'limit_required',
  LIMIT_IS_LOWER_THAN_DEPOSIT:        'limit_lower_than_deposit',
  MONTHLY_LIMIT_IS_LOWER_THAN_WEEKLY: 'monthly_limit_lower_than_weekly',
  WEEKLY_LIMIT_IS_LOWER_THAN_DAILY:   'weekly_limit_lower_than_daily',
  NO_WELCOME_OFFER_NOTIFICATION:      'no_welcome_offer_notification',
  NATIONALITY_IS_REQUIRED:            'nationality_is_required',
  INVALID_JURISDICTION_FOR_BRAND:     'invalid_jurisdiction_for_brand',
  COUNTRY_RESTRICTED:                 'country_restricted',
  PROVINCE_IN_ADDRESS_REQUIRED:       'province_in_address_required',
  INVALID_OCR_DATA:                   'invalid_ocr_data',
  CREDENTIALS_TAKEN:                  'credentials_taken',
  UNIQUE_PLAYER:                      'unique_player',
  SOCIAL_SECURITY_NUMBER_ALREADY_EXISTS:
    'social_security_number_already_exists',
  SELF_EXCLUDED_WITH_NATIONAL_REGISTRY: 'self_excluded_with_national_registry',
  FIRST_NAME_REQUIRED:                  'first_name_required',
  LAST_NAME_REQUIRED:                   'last_name_required',
  INVALID_POSTAL_CODE:                  'invalid_postalcode',
  COUNTRY_OF_RESIDENCE_IS_REQUIRED:     'country_of_residence_is_required',
  PREDICTIONS_GOOGLE_API_NOT_FOUND:     'predictions_google_api_not_found',
  NETWORK_ERROR:                        'network_error',
  KYC_REQUIRED:                         'kyc_required',
  UNKNOWN_ERROR:                        'unknown_error',
  INVALID_IDENTITY:                     'invalid_identity',
  REGISTRATION_CANCELLED:               'registration_cancelled',
  INCORRECT_DETAILS:                    'incorrect_details',
  SOMENTING_WNENT_WRONG:                'something_went_wrong',
  DUPLICATE_ACCOUNT:                    'duplicate_account',
  GENERIC_ERROR:                        'generic_error',
});

export const errorBoundaryIgnoredErrors: string[] = [
  API_ERRORS.EMAIL_TAKEN,
  API_ERRORS.PHONE_NUMBER_ALREADY_EXISTS,
  API_ERRORS.POSTAL_CODE_FORBIDDEN,
  API_ERRORS.LIMIT_TOO_LOW,
  API_ERRORS.LIMIT_TOO_HIGH,
  API_ERRORS.LIMIT_REQUIRED,
  API_ERRORS.INVALID_IDENTITY,
];

export const REGISTRATION_API_RESPONSES = Object.freeze({
  EMAIL_TAKEN:                    'EMAIL_TAKEN',
  INVALID_EMAIL:                  'INVALID_EMAIL',
  EMAIL_FORBIDDEN:                'EMAIL_FORBIDDEN',
  PHONE_NUMBER_ALREADY_EXISTS:    'PHONE_NUMBER_ALREADY_EXISTS',
  VALID:                          'VALID',
  INVALID_JURISDICTION_FOR_BRAND: 'INVALID_JURISDICTION_FOR_BRAND',
  COUNTRY_RESTRICTED:             'COUNTRY_RESTRICTED',
  PROVINCE_IN_ADDRESS_REQUIRED:   'PROVINCE_IN_ADDRESS_REQUIRED',
  INVALID_OCR_DATA:               'INVALID_OCR_DATA',
  CREDENTIALS_TAKEN:              'CREDENTIALS_TAKEN',
  UNIQUE_PLAYER:                  'UNIQUE_PLAYER',
  SOCIAL_SECURITY_NUMBER_ALREADY_EXISTS:
    'SOCIAL_SECURITY_NUMBER_ALREADY_EXISTS',
  TOO_YOUNG:                            'TOO_YOUNG',
  SELF_EXCLUDED_WITH_NATIONAL_REGISTRY: 'SELF_EXCLUDED_WITH_NATIONAL_REGISTRY',
  REGISTRATION_METHOD_COUNTRY_MISMATCH: 'REGISTRATION_METHOD_COUNTRY_MISMATCH',
  CAPTCHA_INVALID_RESPONSE:             'CAPTCHA_INVALID_RESPONSE',
  POSTAL_CODE_FORBIDDEN:                'POSTAL_CODE_FORBIDDEN',
  JURISDICTION_NOT_FOUND:               'JURISDICTION_NOT_FOUND',
  KYC_REQUIRED:                         'KYC_REQUIRED',
  INVALID:                              'INVALID',
  REGISTRATION_CANCELLED:               'REGISTRATION_CANCELLED',
  INCORRECT_DETAILS:                    'INCORRECT_DETAILS',
  DUPLICATE_ACCOUNT:                    'DUPLICATE_ACCOUNT_FOUND',
  GENERIC_ERROR:                        'GENERIC_ERROR',
});

export const BE_FE_ERRORS_MAP = Object.freeze({
  [REGISTRATION_API_RESPONSES.EMAIL_TAKEN]: REGISTRATION_ERRORS.DUPLICATE_EMAIL,
  [REGISTRATION_API_RESPONSES.PHONE_NUMBER_ALREADY_EXISTS]:
  REGISTRATION_ERRORS.DUPLICATE_PHONE,
  [REGISTRATION_API_RESPONSES.INVALID_JURISDICTION_FOR_BRAND]:
  REGISTRATION_ERRORS.INVALID_JURISDICTION_FOR_BRAND,
  [REGISTRATION_API_RESPONSES.COUNTRY_RESTRICTED]:
  REGISTRATION_ERRORS.COUNTRY_RESTRICTED,
  [REGISTRATION_API_RESPONSES.PROVINCE_IN_ADDRESS_REQUIRED]:
  REGISTRATION_ERRORS.PROVINCE_IN_ADDRESS_REQUIRED,
  [REGISTRATION_API_RESPONSES.INVALID_OCR_DATA]:
  REGISTRATION_ERRORS.INVALID_OCR_DATA,
  [REGISTRATION_API_RESPONSES.CREDENTIALS_TAKEN]:
  REGISTRATION_ERRORS.CREDENTIALS_TAKEN,
  [REGISTRATION_API_RESPONSES.UNIQUE_PLAYER]: REGISTRATION_ERRORS.UNIQUE_PLAYER,
  [REGISTRATION_API_RESPONSES.SOCIAL_SECURITY_NUMBER_ALREADY_EXISTS]:
  REGISTRATION_ERRORS.SOCIAL_SECURITY_NUMBER_ALREADY_EXISTS,
  [REGISTRATION_API_RESPONSES.TOO_YOUNG]: REGISTRATION_ERRORS.UNDERAGE,
  [REGISTRATION_API_RESPONSES.SELF_EXCLUDED_WITH_NATIONAL_REGISTRY]:
  REGISTRATION_ERRORS.SELF_EXCLUDED_WITH_NATIONAL_REGISTRY,
  [REGISTRATION_API_RESPONSES.KYC_REQUIRED]:           REGISTRATION_ERRORS.KYC_REQUIRED,
  [REGISTRATION_API_RESPONSES.INVALID_EMAIL]:          REGISTRATION_ERRORS.INCORRECT_EMAIL,
  [REGISTRATION_API_RESPONSES.EMAIL_FORBIDDEN]:        REGISTRATION_ERRORS.EMAIL_FORBIDDEN,
  [REGISTRATION_API_RESPONSES.INVALID]:                REGISTRATION_ERRORS.INVALID_IDENTITY,
  [REGISTRATION_API_RESPONSES.REGISTRATION_CANCELLED]: REGISTRATION_ERRORS.REGISTRATION_CANCELLED,
  [REGISTRATION_API_RESPONSES.INCORRECT_DETAILS]:      REGISTRATION_ERRORS.INCORRECT_DETAILS,
  [REGISTRATION_API_RESPONSES.DUPLICATE_ACCOUNT]:      REGISTRATION_ERRORS.DUPLICATE_ACCOUNT,
  [REGISTRATION_API_RESPONSES.GENERIC_ERROR]:          REGISTRATION_ERRORS.GENERIC_ERROR,
});
